import { BotIcon, ChevronRightIcon, ReplyIcon, XIcon } from "lucide-react";
import { useEffect, useRef } from "react";

import { EmailCard } from "./EmailCard";
import { Column, Row, Text } from "@/components";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { useTaggableUsers } from "@/contexts/TaggableUsersContext";
import { useGetCurrentUser } from "@/hooks/queries/useGetCurrentUser";
import { dates } from "@/lib/dates";
import { cn } from "@/lib/utils";
import { EmailDirection, Email } from "@/types";

const findReplyTarget = (thread: Email[], userId?: number): Email | null => {
  for (let i = thread.length - 1; i >= 0; i--) {
    if (thread[i].rfcMessageId !== null && thread[i].userId === userId) {
      return thread[i];
    }
  }
  return null;
};

export const EmailThreadCard = ({
  activeReplyEmailId,
  candidateFirstName,
  candidateName,
  isOpen,
  isReplyActive,
  onClearReply,
  onReply,
  onToggle,
  thread,
}: {
  activeReplyEmailId?: string;
  candidateFirstName: string;
  candidateName: string;
  isOpen: boolean;
  isReplyActive: boolean;
  onClearReply: () => void;
  // Omitted on read-only surfaces, which drop the reply affordance entirely.
  onReply?: (email: Email) => void;
  onToggle: () => void;
  thread: Email[];
}) => {
  /* Context */
  const { userFirstNameMap } = useTaggableUsers();
  const currentUser = useGetCurrentUser();
  const bottomRef = useRef<HTMLDivElement>(null);

  /* Helpers */
  const firstEmail = thread[0];
  const lastEmail = thread[thread.length - 1];
  const subject = firstEmail.subject || "(no subject)";
  const startedAt = dates.formatShortDate(firstEmail.sentAt);

  const lastIsInbound = lastEmail.direction === EmailDirection.INCOMING;
  const lastIsAgent = lastEmail.sentByAgent && !lastIsInbound;
  // Only a "reply" if something preceded it — a lone initial email was "sent".
  const lastIsReply = thread.length > 1;
  const ownerIsMe = lastEmail.userId === currentUser?.id;
  const ownerFirstName = userFirstNameMap[lastEmail.userId] || "";

  const replyTargetEmail = findReplyTarget(thread, currentUser?.id);
  const replyDisabled = replyTargetEmail === null;

  const handleReply = () => {
    if (replyTargetEmail) onReply?.(replyTargetEmail);
  };

  useEffect(() => {
    if (!isReplyActive || !isOpen) return;
    requestAnimationFrame(() => {
      bottomRef.current?.scrollIntoView({ block: "end", behavior: "smooth" });
    });
  }, [activeReplyEmailId, isOpen, isReplyActive]);

  /* Render */
  const renderIconReplyButton = () => (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex">
            <Button
              aria-label="Reply in thread"
              className="h-7 w-7 p-0 text-slate-500 hover:text-slate-900"
              disabled={replyDisabled}
              onClick={(e) => {
                e.stopPropagation();
                handleReply();
              }}
              type="button"
              variant="ghost"
            >
              <ReplyIcon className="h-3.5 w-3.5" />
            </Button>
          </span>
        </TooltipTrigger>
        {replyDisabled ? (
          <TooltipContent side="top">
            You can only reply to emails you sent or received.
          </TooltipContent>
        ) : null}
      </Tooltip>
    </TooltipProvider>
  );

  const renderExitButton = () => (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex">
            <Button
              aria-label="Exit thread"
              className="h-7 w-7 p-0 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              onClick={onClearReply}
              type="button"
              variant="ghost"
            >
              <XIcon className="h-3.5 w-3.5" />
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent side="top">Exit thread</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  const renderBottomReplyButton = () => (
    <Row className="justify-center pt-3">
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex">
              <Button
                className="h-auto p-0 text-xs font-semibold text-primary-700 hover:bg-transparent hover:text-primary-800 hover:underline"
                disabled={replyDisabled}
                onClick={handleReply}
                type="button"
                variant="ghost"
              >
                Reply in thread
              </Button>
            </span>
          </TooltipTrigger>
          {replyDisabled ? (
            <TooltipContent side="top">
              You can only reply to emails you sent or received.
            </TooltipContent>
          ) : null}
        </Tooltip>
      </TooltipProvider>
    </Row>
  );

  const render = () => (
    <Column className="overflow-hidden">
      <Row className="items-center rounded-lg hover:bg-slate-50">
        <Button
          className="h-auto min-w-0 flex-1 items-center justify-start gap-2 rounded-none bg-transparent px-2.5 py-2 text-left hover:bg-transparent"
          onClick={onToggle}
          type="button"
          variant="ghost"
        >
          <ChevronRightIcon
            className={cn(
              "h-3.5 w-3.5 shrink-0 text-slate-400 transition-transform duration-200",
              isOpen && "rotate-90",
            )}
          />
          <Column className="min-w-0 flex-1 gap-0.5">
            <Row className="items-center gap-2">
              <Text
                variant="header"
                className="truncate text-[13px] leading-tight text-slate-900"
              >
                {subject}
              </Text>
            </Row>
            <Row className="items-center gap-1 leading-tight">
              <Text className="shrink-0 text-[11px] font-normal text-slate-400">
                Started {startedAt}
              </Text>
              <Text className="text-[11px] font-normal text-slate-300">·</Text>
              <ThreadActivityLabel
                candidateFirstName={candidateFirstName}
                isAgent={lastIsAgent}
                isInbound={lastIsInbound}
                isReply={lastIsReply}
                ownerFirstName={ownerFirstName}
                ownerIsMe={ownerIsMe}
              />
            </Row>
          </Column>
        </Button>
        <Row className="shrink-0 items-center gap-1.5 pr-2">
          <Row className="shrink-0 items-center rounded-full bg-slate-100 px-1.5">
            <Text className="text-[10px] font-semibold text-slate-500">
              {thread.length}
            </Text>
          </Row>
          {!onReply ? null : isReplyActive
            ? renderExitButton()
            : renderIconReplyButton()}
        </Row>
      </Row>
      {isOpen ? (
        <Column className="gap-2 px-2.5 py-2.5">
          {thread.map((email) => (
            <EmailCard
              key={email.id}
              candidateName={candidateName}
              email={email}
            />
          ))}
          {!isReplyActive ? renderBottomReplyButton() : null}
          <div ref={bottomRef} />
        </Column>
      ) : null}
    </Column>
  );

  return render();
};

// Viewer-relative last-activity label. Outbound: "{Owner} replied"/"sent" (or
// "You …"), with a robot when the agent sent it — "sent" for a lone initial
// email, "replied" once there's a prior message. Inbound: "{Candidate} sent to
// {Owner}".
const ThreadActivityLabel = ({
  candidateFirstName,
  isAgent,
  isInbound,
  isReply,
  ownerFirstName,
  ownerIsMe,
}: {
  candidateFirstName: string;
  isAgent: boolean;
  isInbound: boolean;
  isReply: boolean;
  ownerFirstName: string;
  ownerIsMe: boolean;
}) => {
  /* Render */
  const render = () => {
    if (isInbound) {
      const recipient = ownerIsMe ? "you" : ownerFirstName;
      return (
        <Text className="truncate text-[11px] font-medium text-slate-500">
          {candidateFirstName} sent to {recipient}
        </Text>
      );
    }

    const verb = isReply ? "replied" : "sent";
    return (
      <Row className="min-w-0 items-center gap-1">
        {isAgent ? (
          <BotIcon className="h-2.5 w-2.5 shrink-0 text-blue-600" />
        ) : null}
        <Text className="truncate text-[11px] font-medium text-slate-500">
          {ownerIsMe ? `You ${verb}` : `${ownerFirstName} ${verb}`}
        </Text>
      </Row>
    );
  };

  return render();
};
